// pages/SubscriptionsPage.tsx
import { useState, useEffect } from "react";
import { Search, Plus, Eye, Edit, Trash2, Ban, CreditCard, Download, RefreshCw } from "lucide-react";
import MenuModal, { Menu } from "../../../components/ui/MenuModal";
import { alertSuccess, alertError } from "../../../helpers/alertError";
import { Subscription, SubscriptionStats } from "../../../types/features/Subscription";
import PageListLayout from "../../../layouts/PageListLayout";
import { useNavigate } from "react-router-dom";
import SubscriptionsList from "../../../components/features/susbscriptions/SubscriptionsList";

// Fake data
const FAKE_SUBSCRIPTIONS: Subscription[] = [
  {
    id: "1",
    entreprise_id: "ent_1",
    entreprise_name: "TechCorp SARL",
    plan: "enterprise",
    status: "active",
    start_date: "2026-01-15",
    end_date: "2027-01-15",
    price: 499,
    currency: "EUR",
    features: ["Support prioritaire", "API illimitée", "Stockage 1TB", "IA avancée"],
    seats: 50,
    seats_used: 42,
    auto_renew: true,
    payment_method: "card",
    last_payment_date: "2026-08-15",
    next_payment_date: "2026-09-15"
  },
  {
    id: "2",
    entreprise_id: "ent_2",
    entreprise_name: "AfriSolutions",
    plan: "professional",
    status: "active",
    start_date: "2026-03-01",
    end_date: "2026-12-01",
    price: 199,
    currency: "XOF",
    features: ["Support standard", "API limitée", "Stockage 100GB"],
    seats: 20,
    seats_used: 15,
    auto_renew: true,
    payment_method: "mobile_money",
    last_payment_date: "2026-07-01",
    next_payment_date: "2026-08-01"
  },
  {
    id: "3",
    entreprise_id: "ent_3",
    entreprise_name: "Digital Africa",
    plan: "starter",
    status: "trial",
    start_date: "2026-08-20",
    end_date: "2026-09-20",
    price: 49,
    currency: "USD",
    features: ["Support basique", "API basique"],
    seats: 5,
    seats_used: 3,
    auto_renew: false,
    payment_method: "card",
    last_payment_date: null,
    next_payment_date: "2026-09-20"
  },
  {
    id: "4",
    entreprise_id: "ent_4",
    entreprise_name: "GreenEnergy",
    plan: "enterprise",
    status: "suspended",
    start_date: "2025-11-01",
    end_date: "2026-11-01",
    price: 599,
    currency: "EUR",
    features: ["Support prioritaire", "API illimitée", "Stockage 1TB"],
    seats: 30,
    seats_used: 28,
    auto_renew: false,
    payment_method: "bank_transfer",
    last_payment_date: "2026-07-01",
    next_payment_date: null
  },
  {
    id: "5",
    entreprise_id: "ent_5",
    entreprise_name: "LogiTrans",
    plan: "professional",
    status: "expired",
    start_date: "2025-08-01",
    end_date: "2026-08-01",
    price: 249,
    currency: "XOF",
    features: ["Support standard", "API limitée"],
    seats: 15,
    seats_used: 12,
    auto_renew: false,
    payment_method: "card",
    last_payment_date: "2026-07-01",
    next_payment_date: null
  },
  {
    id: "6",
    entreprise_id: "ent_6",
    entreprise_name: "HealthTech",
    plan: "enterprise",
    status: "active",
    start_date: "2026-06-01",
    end_date: "2027-06-01",
    price: 799,
    currency: "USD",
    features: ["Support prioritaire", "API illimitée", "Stockage 1TB", "IA avancée", "Déploiement dédié"],
    seats: 100,
    seats_used: 87,
    auto_renew: true,
    payment_method: "card",
    last_payment_date: "2026-08-01",
    next_payment_date: "2026-09-01"
  },
  {
    id: "7",
    entreprise_id: "ent_7",
    entreprise_name: "EduSmart",
    plan: "starter",
    status: "active",
    start_date: "2026-07-15",
    end_date: "2027-07-15",
    price: 29,
    currency: "EUR",
    features: ["Support basique"],
    seats: 10,
    seats_used: 4,
    auto_renew: true,
    payment_method: "mobile_money",
    last_payment_date: "2026-08-15",
    next_payment_date: "2026-09-15"
  }
];

const FAKE_STATS: SubscriptionStats = {
  total_subscriptions: 7,
  active: 4,
  trial: 1,
  suspended: 1,
  expired: 1,
  revenue_total: 2424,
  revenue_monthly: 202,
  growth_percentage: 12.5
};

export default function SubscriptionsPage() {
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState<Subscription[]>(FAKE_SUBSCRIPTIONS);
  const [stats, setStats] = useState<SubscriptionStats>(FAKE_STATS);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<Subscription[]>([]);

  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null);

  useEffect(() => {
    // Simulation de chargement
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
    }, 500);
  }, []);

  const displaySubscriptions = searchQuery.trim() ? searchResults : subscriptions;

  const handleSearch = () => {
    if (searchQuery.trim()) {
      const filtered = subscriptions.filter(sub => 
        sub.entreprise_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        sub.plan.toLowerCase().includes(searchQuery.toLowerCase()) ||
        sub.status.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setSearchResults(filtered);
    } else {
      setSearchResults([]);
    }
  };

  const handleDeleteSubscription = (id: string) => {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer cet abonnement ?")) {
      setSubscriptions(prev => prev.filter(s => s.id !== id));
      alertSuccess("Abonnement supprimé avec succès !");
    }
  };

  const handleToggleStatus = (subscription: Subscription) => {
    const newStatus = subscription.status === "active" ? "suspended" : "active";
    const action = newStatus === "active" ? "activer" : "suspendre";
    
    if (window.confirm(`Êtes-vous sûr de vouloir ${action} cet abonnement ?`)) {
      setSubscriptions(prev => 
        prev.map(s => s.id === subscription.id ? { ...s, status: newStatus as any } : s)
      );
      alertSuccess(`Abonnement ${action} avec succès !`);
    }
  };

  const getActionsMenu = (subscription: Subscription): Menu[] => {
    const isSuspended = subscription.status === "suspended";
    
    return [
      {
        label: "Voir détails",
        icon: Eye,
        onClick: () => {
          navigate("/administration/subscriptions/details", { state: subscription });
          setSelectedSubscription(null);
        },
      },
      {
        label: "Modifier",
        icon: Edit,
        onClick: () => {
          navigate("/administration/subscriptions/edit", { state: subscription });
          setSelectedSubscription(null);
        },
      },
      {
        label: isSuspended ? "Réactiver" : "Suspendre",
        icon: Ban,
        onClick: () => {
          handleToggleStatus(subscription);
          setSelectedSubscription(null);
        },
        className: isSuspended ? "text-green-600 hover:bg-green-50" : "text-amber-600 hover:bg-amber-50",
      },
      {
        label: "Supprimer",
        icon: Trash2,
        onClick: () => {
          handleDeleteSubscription(subscription.id);
          setSelectedSubscription(null);
        },
        className: "text-red-600 hover:bg-red-50",
      },
    ];
  };

  const StatCard = ({ label, value, subValue, color }: any) => (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <p className="text-sm text-gray-500">{label}</p>
      <p className={`text-2xl font-bold ${color || 'text-gray-900'}`}>{value}</p>
      {subValue && <p className="text-xs text-gray-400 mt-1">{subValue}</p>}
    </div>
  );

  return (
    <PageListLayout title="">
      <div className="px-6 py-5 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-bold text-[#031B4E] tracking-tight">Abonnements</h1>
              <p className="text-sm text-slate-400 font-light mt-0.5">
                Gérez tous les abonnements des entreprises
              </p>
              <p className="text-xs text-slate-400 mt-1">
                {stats.total_subscriptions} abonnement(s) au total
              </p>
            </div>
            
            <button
              onClick={() => navigate("/administration/subscriptions/new")}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
            >
              <Plus size={18} />
              <span>Nouvel abonnement</span>
            </button>
          </div>

          {/* Statistiques */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mt-6">
            <StatCard label="Total" value={stats.total_subscriptions} />
            <StatCard label="Actifs" value={stats.active} color="text-green-600" />
            <StatCard label="Essai" value={stats.trial} color="text-blue-600" />
            <StatCard label="Suspendus" value={stats.suspended} color="text-amber-600" />
            <StatCard label="Expirés" value={stats.expired} color="text-red-600" />
            <StatCard 
              label="CA mensuel" 
              value={`${stats.revenue_monthly}€`} 
              subValue={`+${stats.growth_percentage}% vs mois dernier`}
              color="text-indigo-600"
            />
          </div>

          {/* Zone de Recherche */}
          <div className="mt-6">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Rechercher par entreprise, plan ou statut..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                  className="w-full pl-10 pr-4 py-2.5 bg-slate-50/50 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder-slate-400 focus:border-blue-600 focus:bg-white focus:outline-none focus:ring-4 focus:ring-blue-600/5 transition-all"
                />
              </div>
              <button
                onClick={handleSearch}
                className="px-5 py-2.5 bg-primary text-white font-medium text-sm rounded-xl hover:bg-primary/90 transition-all shadow-sm"
              >
                Rechercher
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Liste des abonnements */}
      <div className="px-6 py-8 max-w-7xl mx-auto">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-7 w-7 border-2 border-[#031B4E] border-t-transparent"></div>
          </div>
        ) : (
          <SubscriptionsList
            subscriptions={displaySubscriptions}
            onAction={(subscription) => {
              setSelectedSubscription(subscription);
            }}
            selectable={false}
          />
        )}
      </div>

      {/* Menu modal volant */}
      {selectedSubscription && (
        <MenuModal
          title={`${selectedSubscription.entreprise_name}`}
          isOpen={true}
          onClose={() => setSelectedSubscription(null)}
          icon={null}
          menu={getActionsMenu(selectedSubscription)}
        />
      )}
    </PageListLayout>
  );
}